import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import JsonLd from '@/components/feature/JsonLd';
import { SITE_URL, LOGO_URL } from '@/components/feature/seo';
import { MAIN_SITE_URL } from '@/mocks/site';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

export default function Article() {
  const { t, i18n } = useTranslation();

  const locale = i18n.language === 'en' ? 'en-US' : i18n.language;
  const date = new Date('2026-08-21');
  const formattedDate = date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });

  const toc = [
    { id: 's1', label: t('article.toc1') },
    { id: 's2', label: t('article.toc2') },
    { id: 's3', label: t('article.toc3') },
    { id: 's4', label: t('article.toc4') },
    { id: 's5', label: t('article.toc5') },
    { id: 's6', label: t('article.toc6') },
    { id: 'faq', label: t('article.toc7') },
  ];

  const sections = [
    { id: 's1', title: t('article.s1Title'), p1: t('article.s1p1'), p2: t('article.s1p2') },
    { id: 's2', title: t('article.s2Title'), p1: t('article.s2p1'), p2: t('article.s2p2') },
    { id: 's3', title: t('article.s3Title'), p1: t('article.s3p1'), p2: t('article.s3p2') },
    { id: 's4', title: t('article.s4Title'), p1: t('article.s4p1'), p2: t('article.s4p2') },
    { id: 's5', title: t('article.s5Title'), p1: t('article.s5p1'), p2: t('article.s5p2') },
    { id: 's6', title: t('article.s6Title'), p1: t('article.s6p1'), p2: t('article.s6p2') },
  ];

  const faqs = [
    { q: t('article.fq1'), a: t('article.fa1') },
    { q: t('article.fq2'), a: t('article.fa2') },
    { q: t('article.fq3'), a: t('article.fa3') },
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: t('article.h1'),
    description: t('article.metaDescription'),
    datePublished: '2026-08-21',
    dateModified: '2026-08-21',
    inLanguage: ['en', 'pl', 'es', 'fr'],
    author: { '@type': 'Organization', name: t('common.brand') },
    publisher: {
      '@type': 'Organization',
      name: t('common.brand'),
      logo: { '@type': 'ImageObject', url: LOGO_URL },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/usfans-spreadsheet-guide` },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('article.breadcrumbHome'), item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: t('article.breadcrumbGuides'), item: `${SITE_URL}/usfans-spreadsheet-guide` },
    ],
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: t('article.fq1'), acceptedAnswer: { '@type': 'Answer', text: t('article.fa1') } },
      { '@type': 'Question', name: t('article.fq2'), acceptedAnswer: { '@type': 'Answer', text: t('article.fa2') } },
      { '@type': 'Question', name: t('article.fq3'), acceptedAnswer: { '@type': 'Answer', text: t('article.fa3') } },
    ],
  };

  return (
    <>
      <JsonLd data={articleLd} />
      <JsonLd data={breadcrumbLd} />
      <JsonLd data={faqLd} />
      <div className="flex min-h-screen flex-col bg-background-50">
        <Header />

      <main className="flex-1">
        <div className="mx-auto max-w-3xl px-4 py-10 md:px-6 md:py-14">
          {/* Breadcrumb */}
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700">
              {t('article.breadcrumbHome')}
            </Link>
            <i className="ri-arrow-right-s-line text-sm text-foreground-300" />
            <span className="text-foreground-400">{t('article.breadcrumbGuides')}</span>
          </nav>

          {/* Title + meta */}
          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-foreground-950 md:text-4xl">
            {t('article.h1')}
          </h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line text-sm" />
              {t('article.published')}: {formattedDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-refresh-line text-sm" />
              {t('article.updated')}: {formattedDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line text-sm" />
              {t('article.readingTime')}
            </span>
          </div>

          {/* Lead */}
          <p className="mt-6 text-lg leading-relaxed text-foreground-700">{t('article.lead')}</p>

          {/* Table of contents */}
          <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
            <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-foreground-700">
              {t('article.tocTitle')}
            </h2>
            <ol className="mt-3 flex flex-col gap-2">
              {toc.map((item, i) => (
                <li key={item.id} className="flex items-center gap-2">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
                    {i + 1}
                  </span>
                  <a href={`#${item.id}`} className="text-sm text-foreground-700 transition-colors hover:text-primary-700">
                    {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {/* Sections */}
          <div className="mt-10 flex flex-col gap-10">
            {sections.map((section) => (
              <section key={section.id} id={section.id}>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{section.title}</h2>
                <p className="mt-3 text-base leading-relaxed text-foreground-700">{section.p1}</p>
                <p className="mt-3 text-base leading-relaxed text-foreground-700">{section.p2}</p>
              </section>
            ))}
          </div>

          {/* Internal link back home */}
          <div className="mt-10 rounded-lg border border-primary-200 bg-primary-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">{t('article.relatedTitle')}</h2>
            <p className="mt-2 text-sm text-foreground-600">{t('article.backHomeDesc')}</p>
            <Link
              to="/"
              className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line text-base" />
              {t('article.backHome')}
            </Link>
          </div>

          {/* FAQ */}
          <section id="faq" className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">{t('article.s7Title')}</h2>
            <div className="mt-4 flex flex-col gap-3">
              {faqs.map((faq) => (
                <div key={faq.q} className="rounded-lg border border-background-200 bg-background-100 p-5">
                  <h3 className="font-heading text-base font-semibold text-foreground-950">{faq.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="mt-12 rounded-lg bg-primary-500 p-6 text-center md:p-8">
            <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">{t('article.ctaTitle')}</h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-background-50/90">{t('article.ctaText')}</p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <a
                href={MAIN_SITE_URL}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
              >
                {t('article.ctaExplore')}
                <i className="ri-arrow-right-up-line text-base" />
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
              >
                {t('article.backHome')}
              </Link>
            </div>
          </div>
        </div>
      </main>

        <Footer />
      </div>
    </>
  );
}